import { cn } from "@/lib/utils";

import type { HealthState } from "./api";
import { STATE_LETTER, STATE_WORD } from "./format";

/**
 * A health state as a letter, a shape and a word (KPI v4 H9, UX §7). The one new
 * component in C10: no shadcn primitive encodes status without leaning on colour.
 *
 * The brand has no green or amber token (UX Q1), so colour is at most a second
 * signal: purple for ok, ink for drifting, danger for dark, grey for unknown.
 * Take the colour away and every state still reads — that is the test.
 */
const SHAPE: Record<HealthState, string> = {
  ok: "rounded-full border-solid",
  drifting: "rotate-45 rounded-[2px] border-solid",
  dark: "rounded-[4px] border-solid",
  unknown: "rounded-full border-dashed",
};

const TONE: Record<HealthState, string> = {
  ok: "text-ks-purple",
  drifting: "text-ks-ink",
  dark: "text-ks-danger",
  unknown: "text-ks-grey-1",
};

export function StatusGlyph({ state, showWord = true, className }: { state: HealthState; showWord?: boolean; className?: string }) {
  return (
    <span
      data-state={state}
      data-shape={state === "ok" ? "circle" : state === "drifting" ? "diamond" : state === "dark" ? "square" : "dashed-circle"}
      className={cn("inline-flex items-center gap-1.5 text-xs whitespace-nowrap", TONE[state], className)}
      aria-label={STATE_WORD[state]}
      title={STATE_WORD[state]}
    >
      <span aria-hidden className={cn("inline-flex size-5 items-center justify-center border-[1.5px] border-current text-[11px] font-bold", SHAPE[state])}>
        <span className={state === "drifting" ? "-rotate-45" : undefined}>{STATE_LETTER[state]}</span>
      </span>
      {showWord && <span>{STATE_WORD[state]}</span>}
    </span>
  );
}
